import React from 'react'
import {GQL_SearchType, GQL_StringSearchInput, GQL_UserSearchInput} from '../../generated/graphql'
import {Box} from '../../webui/Box'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../webui/Typography'
import {AllUsersTable} from './AllUsersTable'
import {useAllUsers} from './gql/useAllUsers'
import {UserExport} from './UserExport'

interface Props {
  emailSearch?: string
  nameSearch?: string
}

const paginationLimit = 100

// Temporary compile helper: if client types aren’t regenerated yet,
// this allows using `name` in the search input without TS errors.
// Safe to keep even after you regenerate types.
type UserSearchInputPatched = GQL_UserSearchInput & {
  name?: GQL_StringSearchInput | null
}

export const AllUsers: React.FC<Props> = ({emailSearch, nameSearch}) => {
  const searchInput: UserSearchInputPatched | undefined =
    emailSearch || nameSearch
      ? {
          email: emailSearch
            ? {
                query: emailSearch,
                type: GQL_SearchType.Contains,
              }
            : undefined,
          name: nameSearch
            ? {
                query: nameSearch,
                type: GQL_SearchType.Contains,
              }
            : undefined,
        }
      : undefined

  const {
    data: {totalCount, users},
    loading,
    error,
    fetchMore,
  } = useAllUsers({
    variables: {
      pagination: {
        offset: 0,
        limit: paginationLimit,
      },
      // Cast keeps this working pre/post codegen update.
      search: (searchInput as unknown as GQL_UserSearchInput) || undefined,
    },
  })

  const onFetchMoreClicked = async () => {
    await fetchMore({
      variables: {
        pagination: {
          offset: users.length,
          limit: paginationLimit,
        },
      },
    })
  }

  if (loading) {
    return <LoadingSpinnerContainer />
  }
  if (error) {
    return <div>{error.message}</div>
  }

  const hasReachedPenultimatePage = async () => {
    await onFetchMoreClicked()
  }

  return (
    <Box>
      <Box my={2}>
        <Paper>
          <AllUsersTable hasReachedPenultimatePage={hasReachedPenultimatePage} users={users} />
          <Box alignItems={'flex-end'} display={'flex'} justifyContent={'flex-end'} pb={1} pr={1}>
            <Box ml={2} />
            <Typography variant={'h6'}>Total: {totalCount}</Typography>
          </Box>
        </Paper>
      </Box>
      <UserExport users={users} />
    </Box>
  )
}
